import React, { useState, useEffect } from 'react';
import { DollarSign, Users, Clock, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useEmployees } from '../hooks/useEmployees';
import { useACHPolling } from '../hooks/useACHPolling';
import PayrollHistory from './PayrollHistory';
import type { Employee, TaxSettings } from '../types';
import {
  getNextPayPeriods,
  getPreviousPayPeriods,
  getPeriodsStartingWithNextPayday,
  canEmployeeReceivePaycheck,
  parseLocalDateForDisplay,
  type WorkWeekPeriod
} from '../utils/payrollPeriodCalculator';
import { checkACHEnabled, processACHPayment, getEmployeeBankingStatus } from '../utils/achPaymentService';

interface HoursWorked {
  [employeeId: string]: number;
}

const defaultSettings: TaxSettings = {
  medicareRate: 1.45,
  socialSecurityRate: 6.2
};

const PayrollProcess: React.FC = () => {
  const { employees } = useEmployees();
  const { scheduleInitialCheck } = useACHPolling();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [hoursWorked, setHoursWorked] = useState<HoursWorked>({});
  const [selectedPeriod, setSelectedPeriod] = useState<WorkWeekPeriod | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPeriods, setCurrentPeriods] = useState<WorkWeekPeriod[]>(() => {
    return getPeriodsStartingWithNextPayday(4);
  });
  const [achEnabled, setAchEnabled] = useState(false);
  const [employeeBanking, setEmployeeBanking] = useState<Record<string, boolean>>({});

  const availablePayPeriods = currentPeriods;

  useEffect(() => {
    const loadACHStatus = async () => {
      const enabled = await checkACHEnabled();
      console.log('ACH Status Check - Enabled:', enabled);
      setAchEnabled(enabled);

      if (enabled) {
        const bankingStatus: Record<string, boolean> = {};
        for (const employee of employees) {
          const hasBank = await getEmployeeBankingStatus(employee.id);
          bankingStatus[employee.id] = hasBank;
          console.log(`Banking Status - ${employee.firstName} ${employee.lastName}:`, hasBank);
        }
        setEmployeeBanking(bankingStatus);
        console.log('Final Banking Status Map:', bankingStatus);
      }
    };

    if (employees.length > 0) {
      loadACHStatus();
    }
  }, [employees]);

  const handlePreviousPeriods = () => {
    const firstPeriod = currentPeriods[0];
    const firstPeriodDate = parseLocalDateForDisplay(firstPeriod.workWeekStart);
    const newReferenceDate = new Date(firstPeriodDate);
    newReferenceDate.setDate(newReferenceDate.getDate() - 7);

    const newPeriods = getPreviousPayPeriods(4, newReferenceDate);
    setCurrentPeriods(newPeriods);
    setSelectedPeriod(null);
  };

  const handleNextPeriods = () => {
    const lastPeriod = currentPeriods[currentPeriods.length - 1];
    const lastPeriodDate = parseLocalDateForDisplay(lastPeriod.workWeekEnd);
    const newReferenceDate = new Date(lastPeriodDate);
    newReferenceDate.setDate(newReferenceDate.getDate() + 7);

    const newPeriods = getNextPayPeriods(4, newReferenceDate);
    setCurrentPeriods(newPeriods);
    setSelectedPeriod(null);
  };

  const validatePayPeriod = () => {
    if (!selectedPeriod) {
      throw new Error('Please select a pay period');
    }

    const startDate = parseLocalDateForDisplay(selectedPeriod.workWeekStart);
    const endDate = parseLocalDateForDisplay(selectedPeriod.workWeekEnd);

    if (endDate < startDate) {
      throw new Error('Pay period end date must be after start date');
    }

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid pay period dates');
    }
  };

  const calculatePay = (employee: Employee) => {
    if (!employee.salary || employee.salary <= 0) {
      throw new Error(`Invalid salary for employee ${employee.firstName} ${employee.lastName}`);
    }

    let grossPay: number;

    if (employee.paymentType === 'hourly') {
      const hours = hoursWorked[employee.id] || 0;
      if (hours <= 0) {
        throw new Error(`Please enter hours worked for ${employee.firstName} ${employee.lastName}`);
      }
      grossPay = employee.salary * hours;
    } else {
      const annualSalary = employee.salary;
      grossPay = annualSalary / 52;
    }

    // Add bonus if exists
    const bonus = employee.bonus || 0;
    if (bonus > 0) {
      grossPay += bonus;
    }

    // Round to 2 decimal places
    grossPay = Number(grossPay.toFixed(2));

    // Ensure grossPay is valid and positive
    if (isNaN(grossPay) || grossPay <= 0) {
      throw new Error(`Invalid gross pay calculation for employee ${employee.firstName} ${employee.lastName}`);
    }

    const is1099 = employee.taxStatus === '1099';

    let federalTax = 0;
    let medicareTax = 0;
    let socialSecurityTax = 0;
    let stateTax = 0;

    if (!is1099) {
      const federalRate = employee.federalTaxRate / 100;
      const medicareRate = defaultSettings.medicareRate / 100;
      const socialSecurityRate = defaultSettings.socialSecurityRate / 100;

      federalTax = Number((grossPay * federalRate).toFixed(2));
      medicareTax = Number((grossPay * medicareRate).toFixed(2));
      socialSecurityTax = Number((grossPay * socialSecurityRate).toFixed(2));
      stateTax = Number(employee.stateRate || 0);
    }

    // Use employee-specific insurance and retirement rates
    const insuranceRate = employee.insuranceRate || 0;
    const retirementRate = employee.retirementRate || 0;

    const insuranceDeduction = Number((grossPay * (insuranceRate / 100)).toFixed(2));
    const retirementDeduction = Number((grossPay * (retirementRate / 100)).toFixed(2));

    // Validate all deductions
    if (
      isNaN(federalTax) ||
      isNaN(medicareTax) ||
      isNaN(socialSecurityTax) ||
      isNaN(stateTax) ||
      isNaN(insuranceDeduction) ||
      isNaN(retirementDeduction)
    ) {
      throw new Error(`Invalid deduction calculation for employee ${employee.firstName} ${employee.lastName}`);
    }

    const totalTax = Number((
      federalTax +
      medicareTax +
      socialSecurityTax +
      stateTax
    ).toFixed(2));

    const totalDeductions = Number((
      totalTax +
      insuranceDeduction +
      retirementDeduction
    ).toFixed(2));

    // Ensure we don't have negative net pay
    const netPay = Math.max(0, Number((grossPay - totalDeductions).toFixed(2)));

    // Final validation
    if (isNaN(netPay) || netPay < 0) {
      throw new Error(`Invalid net pay calculation for employee ${employee.firstName} ${employee.lastName}`);
    }

    return {
      grossPay,
      netPay,
      deductions: {
        tax: totalTax,
        federalTax,
        medicareTax,
        socialSecurityTax,
        stateTax,
        insurance: insuranceDeduction,
        retirement: retirementDeduction
      },
      bonus: bonus > 0 ? bonus : undefined
    };
  };

  const processPayroll = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      validatePayPeriod();

      const payrollEntries = await Promise.all(selectedEmployees.map(async employeeId => {
        const employee = employees.find(e => e.id === employeeId);
        if (!employee) {
          throw new Error(`Employee ${employeeId} not found`);
        }

        if (!canEmployeeReceivePaycheck(employee.startDate, selectedPeriod!.workWeekEnd)) {
          throw new Error(
            `${employee.firstName} ${employee.lastName} has not completed a full work week yet. ` +
            `They started on ${parseLocalDateForDisplay(employee.startDate).toLocaleDateString()} and need to work through ` +
            `${parseLocalDateForDisplay(selectedPeriod!.workWeekEnd).toLocaleDateString()} to be eligible for this paycheck.`
          );
        }

        const payCalculation = calculatePay(employee);

        const entry = {
          employee_id: employeeId,
          gross_pay: payCalculation.grossPay,
          net_pay: payCalculation.netPay,
          tax_deduction: payCalculation.deductions.tax,
          federal_tax: payCalculation.deductions.federalTax,
          medicare_tax: payCalculation.deductions.medicareTax,
          social_security_tax: payCalculation.deductions.socialSecurityTax,
          state_tax: payCalculation.deductions.stateTax,
          insurance_deduction: payCalculation.deductions.insurance,
          retirement_deduction: payCalculation.deductions.retirement,
          pay_period_start: selectedPeriod!.workWeekStart,
          pay_period_end: selectedPeriod!.workWeekEnd,
          pay_date: selectedPeriod!.payDate,
          status: 'pending',
          payment_method: 'manual',
          bonus: payCalculation.bonus
        };

        return entry;
      }));

      const { data: insertedEntries, error: dbError } = await supabase
        .from('payroll_entries')
        .insert(payrollEntries)
        .select();

      if (dbError) throw dbError;

      if (achEnabled && insertedEntries) {
        console.log('ACH Processing - Enabled:', achEnabled);
        console.log('ACH Processing - Inserted Entries:', insertedEntries.length);

        const achResults: { employeeId: string; success: boolean; error?: string }[] = [];

        for (const entry of insertedEntries) {
          const employee = employees.find(e => e.id === entry.employee_id);

          console.log(`ACH Processing - Employee: ${employee?.firstName} ${employee?.lastName} (${entry.employee_id})`);
          console.log(`  - Net Pay: $${entry.net_pay}`);

          const hasBanking = await getEmployeeBankingStatus(entry.employee_id);
          console.log(`  - Has Banking (real-time check): ${hasBanking}`);

          if (hasBanking) {
            console.log(`  - Attempting ACH payment...`);
            const result = await processACHPayment(
              entry.id,
              entry.employee_id,
              entry.net_pay
            );

            console.log(`  - ACH Result:`, result);

            achResults.push({
              employeeId: entry.employee_id,
              success: result.success,
              error: result.error
            });

            if (!result.success) {
              console.error(`ACH payment failed for employee ${entry.employee_id}:`, result.error);
            }
          } else {
            console.log(`  - Skipping ACH payment (no banking info)`);
          }
        }

        const failedPayments = achResults.filter(r => !r.success);
        const successfulPayments = achResults.filter(r => r.success);

        console.log(`ACH Summary - Successful: ${successfulPayments.length}, Failed: ${failedPayments.length}`);

        if (successfulPayments.length > 0) {
          console.log(`Successfully processed ${successfulPayments.length} ACH payment(s)`);
          scheduleInitialCheck();
        }

        if (failedPayments.length > 0) {
          const failedEmployees = failedPayments.map(f => {
            const emp = employees.find(e => e.id === f.employeeId);
            const errorMsg = f.error ? ` (${f.error})` : '';
            return emp ? `${emp.firstName} ${emp.lastName}${errorMsg}` : f.employeeId;
          }).join(', ');

          setError(`Payroll created but ACH payment failed for: ${failedEmployees}. These will need to be processed manually.`);
        }
      } else {
        console.log('ACH Processing - Skipped (ACH not enabled or no entries)');
      }

      const employeesToUpdate = selectedEmployees.filter(id => {
        const employee = employees.find(e => e.id === id);
        return employee && employee.bonus > 0;
      });

      if (employeesToUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('employees')
          .update({ bonus: 0 })
          .in('id', employeesToUpdate);

        if (updateError) throw updateError;
      }

      setSuccess(true);
      setSelectedEmployees([]);
      setHoursWorked({});
      setSelectedPeriod(null);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Payroll processing error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred processing payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
      setHoursWorked({});
    } else {
      setSelectedEmployees(employees.map(e => e.id));
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-900">Process Payroll</h2>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-green-500" />
            <div className="text-sm text-green-700">Payroll processed successfully</div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Pay Period</label>
            <div className="flex items-center gap-4">
              <button
                onClick={handlePreviousPeriods}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
                title="Previous Periods"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
                {availablePayPeriods.map((period, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedPeriod(period)}
                    className={`p-4 rounded-lg border ${
                      selectedPeriod?.workWeekStart === period.workWeekStart
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="text-sm font-medium text-gray-900">
                      {period.payYear} Period {period.periodNumber}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Work Week: {parseLocalDateForDisplay(period.workWeekStart).toLocaleDateString()} - {parseLocalDateForDisplay(period.workWeekEnd).toLocaleDateString()}
                    </p>
                    <p className="text-sm font-medium text-blue-600 mt-1">
                      Pay Date: {parseLocalDateForDisplay(period.payDate).toLocaleDateString()}
                    </p>
                  </button>
                ))}
              </div>

              <button
                onClick={handleNextPeriods}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
                title="Next Periods"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Select Employees</label>
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {employees.map(employee => {
                const isEligible = selectedPeriod
                  ? canEmployeeReceivePaycheck(employee.startDate, selectedPeriod.workWeekEnd)
                  : true;

                return (
                  <div key={employee.id} className={`flex items-center justify-between p-3 rounded-lg ${
                    !isEligible && selectedPeriod
                      ? 'bg-red-50 border border-red-200'
                      : 'bg-gray-50'
                  }`}>
                    <label className="flex items-center space-x-3 flex-1">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.id)}
                        disabled={!isEligible && selectedPeriod !== null}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEmployees([...selectedEmployees, employee.id]);
                          } else {
                            setSelectedEmployees(selectedEmployees.filter(id => id !== employee.id));
                            const newHoursWorked = { ...hoursWorked };
                            delete newHoursWorked[employee.id];
                            setHoursWorked(newHoursWorked);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-900">
                          {employee.firstName} {employee.lastName}
                          {employee.taxStatus === '1099' && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              1099
                            </span>
                          )}
                        </span>
                        <p className="text-sm text-gray-500">
                          {employee.position} - {employee.paymentType === 'hourly' ?
                            `$${employee.salary}/hour` :
                            `$${employee.salary.toLocaleString()}/year`}
                          {employee.bonus > 0 && (
                            <span className="ml-2 text-green-600">
                              (Bonus: ${employee.bonus.toLocaleString()})
                            </span>
                          )}
                        </p>
                        {!isEligible && selectedPeriod && (
                          <p className="text-xs text-red-600 mt-1">
                            Not eligible - started {parseLocalDateForDisplay(employee.startDate).toLocaleDateString()},
                            needs to complete work week ending {parseLocalDateForDisplay(selectedPeriod.workWeekEnd).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </label>

                    {employee.paymentType === 'hourly' && selectedEmployees.includes(employee.id) && (
                      <div className="flex items-center space-x-2">
                        <Clock className="h-5 w-5 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={hoursWorked[employee.id] || ''}
                          onChange={(e) => setHoursWorked({
                            ...hoursWorked,
                            [employee.id]: parseFloat(e.target.value) || 0
                          })}
                          placeholder="Hours"
                          className="w-24 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={processPayroll}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <DollarSign className="h-4 w-4" />
              {loading ? 'Processing...' : 'Process Payroll'}
            </button>
          </div>
        </div>
      </div>

      <PayrollHistory key={refreshKey} />
    </div>
  );
};

export default PayrollProcess;